import React, { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase, handleSupabaseError } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/use-toast";
import { useNavigate } from "react-router-dom";
import { Mail, Lock, Loader2, ArrowRight, Phone } from "lucide-react";
import { motion } from "framer-motion";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { logFormSubmission } from "@/services/formSubmissions";

interface LoginFormProps {
  redirectUrl?: string | null;
}

const LoginForm: React.FC<LoginFormProps> = ({ redirectUrl }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotMessage] = useState("Reset Password");
  const [forgotSubmitting, setForgotSubmitting] = useState(false);
  const navigate = useNavigate();

  const trimmedForgotEmail = useMemo(() => forgotEmail.trim(), [forgotEmail]);
  const forgotDisabled = useMemo(() => {
    if (!trimmedForgotEmail) return true;
    return false;
  }, [trimmedForgotEmail]);

  const handleOpenForgot = () => {
    setForgotEmail(email);
    setForgotOpen(true);
  };

  const handleForgotSubmit = async () => {
    if (!trimmedForgotEmail) {
      toast({
        title: "Email required",
        description: "Please enter the email you want to reset.",
        variant: "destructive",
      });
      return;
    }

    setForgotSubmitting(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(trimmedForgotEmail);
      if (error) throw error;

      // Attempt to log the submission but do not block success flow
      const { error: logError } = await logFormSubmission({
        formType: "forgot_password",
        email: trimmedForgotEmail,
        message: forgotMessage,
      });

      if (logError) {
        console.warn("Failed to log form submission:", logError);
      }

      toast({
        title: "Reset request received",
        description: "Expect password reset instructions via email and phone asap, you can also call or text 9546050748.",
      });

      setForgotOpen(false);
    } catch (error) {
      const message = handleSupabaseError(error);
      toast({
        title: "Reset failed",
        description: message,
        variant: "destructive",
      });
    } finally {
      setForgotSubmitting(false);
    }
  };

  async function handleSignIn() {
    try {
      setLoading(true);
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;
      
      toast({
        title: "Welcome back!",
        description: "You have been successfully signed in.",
      });
      
      // Navigate to the redirect URL if provided, otherwise go to home
      const destination = redirectUrl || "/";
      navigate(destination);
    } catch (error) {
      const errorMessage = handleSupabaseError(error);
      toast({
        title: "Sign In Failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSignIn();
  };

  return (
    <>
      <form onSubmit={handleSubmit} autoComplete="on" className="space-y-6">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="space-y-2"
        >
          <Label htmlFor="email" className="text-white/80 font-light">
            Email Address
          </Label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
            <Input
              id="email"
              type="email"
              autoComplete="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
              className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/40 focus:border-emerald-500/50 focus:ring-emerald-500/20 h-12 rounded-xl"
            />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="space-y-2"
        >
          <Label htmlFor="password" className="text-white/80 font-light">
            Password
          </Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
              className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/40 focus:border-emerald-500/50 focus:ring-emerald-500/20 h-12 rounded-xl"
            />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="space-y-3"
        >
          <div className="flex items-center justify-between">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input 
                type="checkbox" 
                className="w-4 h-4 rounded border-white/20 bg-white/5 text-emerald-500 focus:ring-emerald-500/20"
              />
              <span className="text-white/60 text-sm font-light">Remember me</span>
            </label>
            
            <button
              type="button"
              onClick={handleOpenForgot}
              className="text-emerald-400 hover:text-emerald-300 text-sm font-light transition-colors"
            >
              Forgot password?
            </button>
          </div>
          
          <a
            href="tel:9546050748"
            className="flex items-center justify-center gap-2 text-white/70 hover:text-white text-sm font-light transition-colors bg-white/5 hover:bg-white/10 rounded-lg py-2 px-4 border border-white/10"
          >
            <Phone className="h-4 w-4" />
            <span>I want assistance over the phone</span>
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Button
            type="submit"
            disabled={loading}
            className="w-full h-12 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-medium rounded-xl shadow-lg hover:shadow-emerald-500/25 transition-all duration-300 border-0"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Signing in...
              </>
            ) : (
              <>
                Sign In
                <ArrowRight className="ml-2 h-4 w-4" />
              </>
            )}
          </Button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-center"
        >
          <p className="text-white/60 text-sm font-light">
            Don't have an account?{" "}
            <button
              type="button"
              className="text-emerald-400 hover:text-emerald-300 font-medium transition-colors"
              onClick={() => {
                window.dispatchEvent(new CustomEvent('auth:open-register'));
              }}
            >
              Sign up
            </button>
          </p>
        </motion.div>
      </form>

      <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
        <DialogContent className="bg-slate-900 text-white border-white/10">
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
          <DialogDescription className="text-white/70">
            We&apos;ll send reset instructions to the address you provide. Expect updates asap via your email and phone number on file. You can also call or text 954-605-0748 for immediate assistance.
          </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="forgot-email" className="text-white/80 font-light">
                Email Address
              </Label>
              <Input
                id="forgot-email"
                type="email"
                value={forgotEmail}
                onChange={(event) => setForgotEmail(event.target.value)}
                placeholder="you@example.com"
                autoFocus
                className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
                disabled={forgotSubmitting}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="forgot-message" className="text-white/80 font-light">
                Message
              </Label>
              <Textarea
                id="forgot-message"
                value={forgotMessage}
                readOnly
                className="bg-white/5 border-white/10 text-white"
              />
              <p className="text-xs text-white/50">
                This request is logged in the admin reports dashboard so our team can assist quickly.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setForgotOpen(false)}
              disabled={forgotSubmitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleForgotSubmit}
              disabled={forgotDisabled || forgotSubmitting}
              className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500"
            >
              {forgotSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                "Send reset link"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default LoginForm;